import { type PartialDeep, type SetOptional } from 'type-fest';

import { type CanvasElementType, type PartialPropsOf } from '@blaze-runtime/engine-domain';
import { type TagId } from '@blaze-shared/api-contracts-domain';
import {
  type ContainerActionConfig,
  type ElementRedirect,
  type ElementRedirectTargetType,
  type PropertiesConfigInputContainerItemActionModel,
  type PropertiesConfigInputContainerItemUnifiedBulkActionModel,
  type PropertiesConfigInputContainerItemUnifiedOtherActionModel,
  type PropertiesConfigInputContainerItemUnifiedRowActionModel,
  type VisibilityAccessRoleRef,
  type WithRowActions,
  type WithRowClickAction,
  type WithUnifiedBulkActions,
  type WithUnifiedOtherActions,
  type WithUnifiedRowActions,
} from '@blaze-shared/elements-domain';
import { isDefined } from '@blaze-shared/shared-util';

import { arePropsWithVisibility } from './are-props-with-visibility';
import { hasVisibilityConditions } from './element-props-guards';

//#region Action Page Refs

export function extractActionsPageRefs(
  rowActions: PartialDeep<WithRowActions> | undefined,
): string[] {
  if (!rowActions) {
    return [];
  }

  const refs = [
    ...('rowClick' in rowActions ? getRowClickPageRefs(rowActions as WithRowClickAction) : []),
    getActionPageRef(rowActions.addConversation),
    getActionPageRef(rowActions.deleteConversation),
    getActionPageRef(rowActions.editConversation),
    getActionPageRef(rowActions.leaveConversation),
    getActionPageRef(rowActions.itemAdd),
    getActionPageRef(rowActions.itemEdit),
    getActionPageRef(rowActions.itemDelete),
    getActionPageRef(rowActions.itemView),
    getActionPageRef(rowActions.itemViewDialog),
    getActionPageRef(rowActions.sendMessage),
  ];
  return refs.filter(isDefined);
}

function getActionPageRef(
  action: SetOptional<ElementRedirect, 'enabled'> | undefined | string,
): string | undefined {
  if (!action) {
    return undefined;
  }
  if (typeof action === 'string') {
    return action;
  }

  const { targetType, targetId } = action;
  return targetType === 'Dialog' || targetType === 'Drawer' || targetType === 'Page'
    ? targetId
    : undefined;
}

function getRowClickPageRefs({ rowClick }: WithRowClickAction): string[] {
  if (!rowClick?.targetId) {
    return [];
  }
  return Object.values(rowClick.targetId).map(getActionPageRef).filter(isDefined);
}

//#endregion

//#region Access Roles Visibility Refs

export function extractElementVisibilityAccessRoles<T extends PartialPropsOf<CanvasElementType>>(
  props: T,
): VisibilityAccessRoleRef | undefined {
  if (!arePropsWithVisibility(props) || !props.content?.visible?.accessRoles?.length) {
    return undefined;
  }
  const result: VisibilityAccessRoleRef = {
    roles: props.content.visible.accessRoles,
    workflowId: null,
  };
  return result;
}

type ElementRowActions =
  | PartialDeep<
      | WithRowActions
      | WithUnifiedRowActions
      | WithUnifiedBulkActions
      | WithUnifiedOtherActions
      | WithRowClickAction
    >
  | undefined;

export function extractElementActionWorkflowsRefs(
  rowActions: ElementRowActions,
  options?: {
    allowUndefinedTargetTypeForItemDelete?: boolean;
  },
): VisibilityAccessRoleRef[] {
  if (!rowActions) {
    return [];
  }
  const refs: (VisibilityAccessRoleRef | undefined)[] = [
    getActionWorkflowRef(rowActions, 'addConversation'),
    getActionWorkflowRef(rowActions, 'deleteConversation'),
    getActionWorkflowRef(rowActions, 'editConversation'),
    getActionWorkflowRef(rowActions, 'leaveConversation'),
    getActionWorkflowRef(rowActions, 'itemAdd'),
    getActionWorkflowRef(rowActions, 'itemEdit'),
    getActionWorkflowRef(rowActions, 'itemDelete', options?.allowUndefinedTargetTypeForItemDelete),
    getActionWorkflowRef(rowActions, 'itemView'),
    getActionWorkflowRef(rowActions, 'itemViewDialog'),
    getActionWorkflowRef(rowActions, 'sendMessage'),
  ];

  if ('rowClick' in rowActions) {
    refs.push(...getRowClickWorkflowsRefs(rowActions));
  }
  if ('unifiedRowActions' in rowActions && rowActions.unifiedRowActions) {
    for (const action of rowActions.unifiedRowActions) {
      refs.push(getUnifiedActionsAccessRoles(action, rowActions));
    }
  }
  if ('unifiedBulkActions' in rowActions && rowActions.unifiedBulkActions) {
    for (const action of rowActions.unifiedBulkActions) {
      refs.push(getUnifiedActionsAccessRoles(action, rowActions, true));
    }
  }
  if ('unifiedOtherActions' in rowActions && rowActions.unifiedOtherActions) {
    for (const action of rowActions.unifiedOtherActions) {
      refs.push(getUnifiedActionsAccessRoles(action, rowActions));
    }
  }

  if (
    'actions' in rowActions &&
    rowActions.actions &&
    typeof rowActions.actions === 'object' &&
    'items' in rowActions.actions
  ) {
    const actions = rowActions.actions.items as PropertiesConfigInputContainerItemActionModel[];
    for (const action of actions) {
      if (action.action?.workflowId || action.visible?.accessRoles?.length) {
        refs.push({
          workflowId: action.action?.workflowId,
          roles: action.visible?.accessRoles ?? [],
        });
      }
    }
  }

  return refs.filter(isDefined);
}

export function extractElementContentActionWorkflowRef(
  action: ContainerActionConfig | undefined,
): VisibilityAccessRoleRef[] {
  const workflowId = action?.workflowId;
  const actionRefs = workflowId ? [{ workflowId, roles: [] }] : [];
  return actionRefs;
}

function getActionWorkflowRef(
  rowActions: ElementRowActions,
  actionKey: string,
  allowUndefinedTargetType?: boolean,
): VisibilityAccessRoleRef | undefined {
  if (!rowActions || !(actionKey in rowActions)) {
    return undefined;
  }
  const action: SetOptional<ElementRedirect, 'enabled'> | undefined | string = (
    rowActions as unknown as any
  )[actionKey];
  if (!action || typeof action === 'string') {
    return undefined;
  }
  const workflowId = getActionWorkflowId(action, allowUndefinedTargetType);
  if (!workflowId) {
    return undefined;
  }
  const roles = getVisibilityConditionActionRoles(action);
  return { workflowId, roles };
}

function getActionWorkflowId(
  action: {
    targetId?: string;
    targetType?: ElementRedirectTargetType;
  },
  allowUndefinedTargetType?: boolean,
): string | undefined {
  if (action.targetType === 'Workflow') {
    return action.targetId;
  }
  if (action.targetId && !action.targetType && allowUndefinedTargetType) {
    return action.targetId;
  }
  return undefined;
}

function getRowClickWorkflowsRefs({ rowClick }: WithRowClickAction): VisibilityAccessRoleRef[] {
  if (!rowClick?.targetId) {
    return [];
  }
  return Object.values(rowClick.targetId)
    .filter(
      (action: string | ElementRedirect | undefined): action is ElementRedirect =>
        !!action &&
        typeof action !== 'string' &&
        action.enabled &&
        action.targetType === 'Workflow' &&
        !!action.targetId,
    )
    .map((action: ElementRedirect) => ({
      workflowId: action.targetId,
      roles: getVisibilityConditionActionRoles(rowClick),
    }));
}

function getVisibilityConditionActionRoles(action: unknown): TagId[] {
  if (hasVisibilityConditions(action) && action.visibilityConditions.accessRoles) {
    return action.visibilityConditions.accessRoles;
  }
  return [];
}

function getUnifiedActionsAccessRoles(
  action:
    | PropertiesConfigInputContainerItemUnifiedRowActionModel
    | PropertiesConfigInputContainerItemUnifiedBulkActionModel
    | PropertiesConfigInputContainerItemUnifiedOtherActionModel,
  rowActions: any,
  isBulk = false,
): VisibilityAccessRoleRef | undefined {
  let workflowId = action.action?.workflowId;
  let rowAction:
    | {
        targetId?: string;
        targetType?: ElementRedirectTargetType;
      }
    | undefined;

  if (!workflowId) {
    switch (action.type) {
      case 'delete':
        rowAction = isBulk ? rowActions.bulkDelete : rowActions.itemDelete;
        break;
      case 'edit':
        rowAction = isBulk ? rowActions.bulkEdit : rowActions.itemEdit;
        break;
      case 'view':
        rowAction = rowActions.itemView;
        break;
      case 'export':
        rowAction = rowActions.bulkExport;
        break;
    }

    if (rowAction) {
      workflowId = getActionWorkflowId(rowAction, isBulk);
    }
  }

  if (!workflowId) {
    return undefined;
  }

  const roles = action.visible?.accessRoles ?? [];
  return { workflowId, roles };
}

//#endregion
